/**
 * Centralized section descriptions for community details
 * These descriptions explain what each section does and help users understand the features
 */

export const SECTION_DESCRIPTIONS = {
  AUTHENTICATION_PROVIDERS: {
    title: 'About Authentication Providers',
    content: `Authentication providers enable secure access to your community groups and their resources by connecting external identity systems (like Azure AD, Google, or other OAuth2 providers) with the platform.

**How Authentication Providers Work:**
• **Secure Access Control** - Users must authenticate through registered providers before accessing community resources
• **Identity Validation** - The system validates user identity and permissions through external OAuth2 providers
• **Multi-Organization Support** - Different community groups can use different authentication providers based on their needs
• **API Protection** - Only pre-approved OAuth2 applications can access your community's APIs and resources

**Key Features:**
• **Automatic Configuration** - System fetches OpenID Connect settings from provider URLs for seamless integration
• **Role-Based Access** - Works with the platform's role-based access system to determine user permissions
• **Approval Workflow** - New providers start with "Pending" status and require system administrator approval
• **Flexible Management** - System administrators can manage all providers, while regular users can create providers that require approval

**Provider Status:**
• **Pending** - Newly created providers awaiting system administrator approval
• **Approved** - Active providers ready for use by community groups
• **Default** - The primary authentication provider for the community group

Authentication providers are essential for maintaining secure, scalable access control across your community ecosystem.`,
  },
  STANDARD_LABELS: {
    title: 'About Standard Labels',
    content: `Standard labels are predefined content classification categories that help organize and manage content across your communities by providing a consistent way to categorize and identify different types of content for moderation, reporting, and automated processing.

**How Standard Labels Work:**
• **Content Classification** - Labels define specific content categories with clear policies and guidelines
• **Unique Identification** - Each label has a unique identifier for consistent reference across systems
• **Scoped Access** - Labels can be globally available or restricted to specific community groups
• **Automated Processing** - Content can be automatically or manually tagged during moderation workflows

**Key Features:**
• **Standardized Categories** - Ensures uniform content handling and reporting across all communities
• **Approval Workflow** - New labels start as "pending" and require system administrator approval
• **Policy Guidelines** - Each label includes detailed descriptions to guide proper content classification
• **Access Control** - Users only see labels that are globally available or scoped to their community groups

**Label Status:**
• **Pending** - Newly created labels awaiting system administrator approval before becoming active
• **Approved** - Active labels ready for use in content classification and moderation workflows

**Benefits:**
• **Consistency** - Uniform content categorization across all your communities
• **Automation** - Enables automated content processing and routing based on classification
• **Reporting** - Standardized labels improve content analytics and reporting capabilities
• **Moderation** - Helps both automated systems and human moderators classify content properly

Standard labels are essential for maintaining organized, efficient content management and moderation across your community ecosystem.`,
  },
  SECURITY_GROUPS: {
    title: 'About Security Groups',
    content: `Security groups define role-based access control for your community by mapping external authentication groups to platform roles. They determine which users can perform administrative, moderation, or general user actions within your community.

**How Security Groups Work:**
• **Role-Based Access** - Map external identity provider groups (like Active Directory groups) to platform roles
• **Hierarchical Permissions** - Admin groups have full access, moderator groups have content management access, user groups have basic access
• **External Integration** - Works with your authentication providers to validate group membership
• **Dynamic Authorization** - User permissions are determined in real-time based on their group memberships

**Key Features:**
• **Flexible Mapping** - Multiple external groups can map to the same role for organizational flexibility
• **Real-Time Updates** - Changes to user group memberships in your identity provider are reflected immediately
• **Audit Trail** - Track which groups have access to which roles for compliance and security
• **Inheritance Model** - Users with admin groups automatically inherit moderator and user permissions

**Role Types:**
• **Admin Groups** - Full community management access including user management, configuration, and deletion
• **Moderator Groups** - Content moderation, workflow management, and community oversight capabilities
• **User Groups** - Basic community participation and content interaction permissions

**Best Practices:**
• **Principle of Least Privilege** - Only assign the minimum required role for each group
• **Regular Review** - Periodically audit group assignments to ensure they remain appropriate
• **Clear Naming** - Use descriptive group names that clearly indicate their purpose and scope
• **Documentation** - Maintain records of which external groups correspond to which roles

Security groups are essential for maintaining secure, scalable access control that integrates seamlessly with your existing identity management infrastructure.`,
  },
} as const;
